/**
 * eBay-Sites, die PAT kennt. Quelle der IDs und Länderkürzel: Legacy
 * `live_update/action/item.php` (23 Einträge). Domain, GlobalID und Währung sind
 * die bekannten eBay-Zuordnungen; vor der ersten Produktivnutzung einer Site gegen
 * die eBay-Dokumentation prüfen (siehe docs/EBAY_API.md, Offen).
 */
export interface EbaySite {
  readonly id: number;
  /** Zweibuchstabiges Länderkürzel wie im Legacy-Flaggenmapping. */
  readonly country: string;
  readonly globalId: string;
  readonly domain: string;
  readonly currency: string;
  readonly name: string;
}

export const EBAY_SITES: readonly EbaySite[] = [
  { id: 0, country: 'us', globalId: 'EBAY-US', domain: 'ebay.com', currency: 'USD', name: 'eBay United States' },
  { id: 2, country: 'ca', globalId: 'EBAY-ENCA', domain: 'ebay.ca', currency: 'CAD', name: 'eBay Canada (English)' },
  { id: 3, country: 'gb', globalId: 'EBAY-GB', domain: 'ebay.co.uk', currency: 'GBP', name: 'eBay UK' },
  { id: 15, country: 'au', globalId: 'EBAY-AU', domain: 'ebay.com.au', currency: 'AUD', name: 'eBay Australia' },
  { id: 16, country: 'at', globalId: 'EBAY-AT', domain: 'ebay.at', currency: 'EUR', name: 'eBay Austria' },
  { id: 23, country: 'be', globalId: 'EBAY-FRBE', domain: 'befr.ebay.be', currency: 'EUR', name: 'eBay Belgium (French)' },
  { id: 71, country: 'fr', globalId: 'EBAY-FR', domain: 'ebay.fr', currency: 'EUR', name: 'eBay France' },
  { id: 77, country: 'de', globalId: 'EBAY-DE', domain: 'ebay.de', currency: 'EUR', name: 'eBay Germany' },
  { id: 100, country: 'us', globalId: 'EBAY-MOTOR', domain: 'ebay.com', currency: 'USD', name: 'eBay Motors' },
  { id: 101, country: 'it', globalId: 'EBAY-IT', domain: 'ebay.it', currency: 'EUR', name: 'eBay Italy' },
  { id: 123, country: 'be', globalId: 'EBAY-NLBE', domain: 'benl.ebay.be', currency: 'EUR', name: 'eBay Belgium (Dutch)' },
  { id: 146, country: 'nl', globalId: 'EBAY-NL', domain: 'ebay.nl', currency: 'EUR', name: 'eBay Netherlands' },
  { id: 186, country: 'es', globalId: 'EBAY-ES', domain: 'ebay.es', currency: 'EUR', name: 'eBay Spain' },
  { id: 193, country: 'ch', globalId: 'EBAY-CH', domain: 'ebay.ch', currency: 'CHF', name: 'eBay Switzerland' },
  { id: 201, country: 'hk', globalId: 'EBAY-HK', domain: 'ebay.com.hk', currency: 'HKD', name: 'eBay Hong Kong' },
  { id: 203, country: 'in', globalId: 'EBAY-IN', domain: 'ebay.in', currency: 'INR', name: 'eBay India' },
  { id: 205, country: 'ie', globalId: 'EBAY-IE', domain: 'ebay.ie', currency: 'EUR', name: 'eBay Ireland' },
  { id: 207, country: 'my', globalId: 'EBAY-MY', domain: 'ebay.com.my', currency: 'MYR', name: 'eBay Malaysia' },
  { id: 210, country: 'ca', globalId: 'EBAY-FRCA', domain: 'cafr.ebay.ca', currency: 'CAD', name: 'eBay Canada (French)' },
  { id: 211, country: 'ph', globalId: 'EBAY-PH', domain: 'ebay.ph', currency: 'PHP', name: 'eBay Philippines' },
  { id: 212, country: 'pl', globalId: 'EBAY-PL', domain: 'ebay.pl', currency: 'PLN', name: 'eBay Poland' },
  { id: 216, country: 'sg', globalId: 'EBAY-SG', domain: 'ebay.com.sg', currency: 'SGD', name: 'eBay Singapore' },
  { id: 218, country: 'se', globalId: 'EBAY-SE', domain: 'ebay.se', currency: 'SEK', name: 'eBay Sweden' },
] as const;

const byId = new Map<number, EbaySite>(EBAY_SITES.map(site => [site.id, site]));

export function ebaySite(id: number): EbaySite | undefined {
  return byId.get(id);
}

export function isKnownEbaySite(id: number): boolean {
  return byId.has(id);
}

/** eBay-Umgebung; serverseitig aus der Stage-Konfiguration gebunden, nie aus einer Anfrage. */
export type EbayEnvironment = 'production' | 'sandbox';

/**
 * Öffentliche Artikel-URL einer Site; Motors nutzt die US-Domain. Sandbox-Angebote (Festlegung 25.10.2026, Demo 1)
 * liegen unter `www.sandbox.<Site-Domain>`; Codex prüft den Linkvertrag gegen die eBay-Sandbox und meldet Abweichungen.
 */
export function ebayItemUrl(site: number, ebayItemId: string, environment: EbayEnvironment = 'production'): string {
  const domain = byId.get(site)?.domain ?? 'ebay.com';
  return `https://www.${environment === 'sandbox' ? 'sandbox.' : ''}${domain}/itm/${ebayItemId}`;
}
